import {
  DestroyRef,
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Translation } from 'primeng/api';
import { PrimeNG } from 'primeng/config';

export type PrimeNgLanguage = 'nl' | 'fr' | 'en';

const DATE_FORMAT = 'dd/mm/yy';

/** The PrimeNG texts the apps show: the calendar (with its button labels) and empty dropdowns. Weeks start on Monday. */
export const PRIMENG_LOCALES: Record<PrimeNgLanguage, Translation> = {
  nl: {
    dayNames: ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'],
    dayNamesShort: ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'],
    dayNamesMin: ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'],
    monthNames: [
      'januari',
      'februari',
      'maart',
      'april',
      'mei',
      'juni',
      'juli',
      'augustus',
      'september',
      'oktober',
      'november',
      'december',
    ],
    monthNamesShort: ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
    firstDayOfWeek: 1,
    dateFormat: DATE_FORMAT,
    today: 'Vandaag',
    clear: 'Wissen',
    weekHeader: 'Wk',
    chooseDate: 'Kies een datum',
    chooseMonth: 'Kies een maand',
    chooseYear: 'Kies een jaar',
    prevDecade: 'Vorig decennium',
    nextDecade: 'Volgend decennium',
    prevYear: 'Vorig jaar',
    nextYear: 'Volgend jaar',
    prevMonth: 'Vorige maand',
    nextMonth: 'Volgende maand',
    emptyMessage: 'Geen resultaten gevonden',
    emptyFilterMessage: 'Geen resultaten gevonden',
  },
  fr: {
    dayNames: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
    dayNamesShort: ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'],
    dayNamesMin: ['di', 'lu', 'ma', 'me', 'je', 've', 'sa'],
    monthNames: [
      'janvier',
      'février',
      'mars',
      'avril',
      'mai',
      'juin',
      'juillet',
      'août',
      'septembre',
      'octobre',
      'novembre',
      'décembre',
    ],
    monthNamesShort: [
      'janv.',
      'févr.',
      'mars',
      'avr.',
      'mai',
      'juin',
      'juil.',
      'août',
      'sept.',
      'oct.',
      'nov.',
      'déc.',
    ],
    firstDayOfWeek: 1,
    dateFormat: DATE_FORMAT,
    today: "Aujourd'hui",
    clear: 'Effacer',
    weekHeader: 'Sem',
    chooseDate: 'Choisir une date',
    chooseMonth: 'Choisir un mois',
    chooseYear: 'Choisir une année',
    prevDecade: 'Décennie précédente',
    nextDecade: 'Décennie suivante',
    prevYear: 'Année précédente',
    nextYear: 'Année suivante',
    prevMonth: 'Mois précédent',
    nextMonth: 'Mois suivant',
    emptyMessage: 'Aucun résultat trouvé',
    emptyFilterMessage: 'Aucun résultat trouvé',
  },
  en: {
    dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    monthNames: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    firstDayOfWeek: 1,
    dateFormat: DATE_FORMAT,
    today: 'Today',
    clear: 'Clear',
    weekHeader: 'Wk',
    chooseDate: 'Choose a date',
    chooseMonth: 'Choose a month',
    chooseYear: 'Choose a year',
    prevDecade: 'Previous decade',
    nextDecade: 'Next decade',
    prevYear: 'Previous year',
    nextYear: 'Next year',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    emptyMessage: 'No results found',
    emptyFilterMessage: 'No results found',
  },
};

/**
 * Gives PrimeNG the texts of the language the translation service uses, and switches them along with it. The
 * backoffice only ever uses Dutch; the portal follows the portal language.
 */
export function providePrimeNgLocale(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => {
      const primeNg = inject(PrimeNG);
      const translate = inject(TranslateService);
      const apply = (language: string | undefined) =>
        primeNg.setTranslation(PRIMENG_LOCALES[language as PrimeNgLanguage] ?? PRIMENG_LOCALES.nl);

      apply(translate.currentLang);
      translate.onLangChange.pipe(takeUntilDestroyed(inject(DestroyRef))).subscribe(({ lang }) => apply(lang));
    }),
  ]);
}
